export type BulkField = 'id' | 'label' | 'status' | 'role' | 'tenant'

export type BulkEntity = {
  id: string
  label?: string
  dcCode?: string
  hallId?: string | null
  moduleId?: string | null
  rowId?: string | null
  revision?: number
}

export type BulkScope = {
  kind: 'datacenter' | 'hall' | 'module' | 'row' | 'saved_query'
  value?: string
  query?: string
}

export type TableImport = {
  headers: string[]
  rows: string[][]
  delimiter: '\t' | ',' | ';'
  errors: string[]
}

export type SavedQuerySnapshot = {
  ids: string[]
  fingerprint: string
}

function parseLine(text: string, delimiter: string): string[] {
  const cells: string[] = []
  let cell = ''
  let quoted = false
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '"' && text[index + 1] === '"' && quoted) {
      cell += '"'
      index += 1
    } else if (char === '"') {
      quoted = !quoted
    } else if (char === delimiter && !quoted) {
      cells.push(cell.trim())
      cell = ''
    } else {
      cell += char
    }
  }
  cells.push(cell.trim())
  return cells
}

export function parsePastedTable(source: string): TableImport {
  const lines = source.replace(/^\uFEFF/, '').split(/\r?\n/).filter(line => line.trim())
  if (!lines.length) return { headers: [], rows: [], delimiter: '\t', errors: ['Вставьте хотя бы одну строку с заголовками'] }
  const delimiter = [...(['\t', ';', ','] as const)].sort((a: string, b: string) =>
    (lines[0].split(b).length - 1) - (lines[0].split(a).length - 1),
  )[0]
  const headers = parseLine(lines[0], delimiter)
  const errors = headers.some(header => !header) ? ['Заголовки столбцов не могут быть пустыми'] : []
  const rows = lines.slice(1).map(line => {
    const cells = parseLine(line, delimiter)
    return headers.map((_header, index) => cells[index] ?? '')
  })
  return { headers, rows, delimiter, errors }
}

export function mapImportedRows(
  table: TableImport,
  mapping: Partial<Record<BulkField, string>>,
): { records: Array<Record<BulkField, string>>; errors: string[] } {
  const errors = [...table.errors]
  const selected = Object.values(mapping).filter(Boolean)
  if (!mapping.id && !mapping.label) errors.push('Сопоставьте столбец с ID или меткой стойки')
  if (new Set(selected).size !== selected.length) errors.push('Один столбец нельзя сопоставить с несколькими полями')
  const records: Array<Record<BulkField, string>> = errors.length ? [] : table.rows.map(row => Object.fromEntries(
    (Object.entries(mapping) as Array<[BulkField, string | undefined]>)
      .filter((entry): entry is [BulkField, string] => Boolean(entry[1]))
      .map(([field, header]) => [field, row[table.headers.indexOf(header)] ?? '']),
  ) as Record<BulkField, string>)
  return { records, errors }
}

export function filterBulkScope(entities: BulkEntity[], scope: BulkScope): BulkEntity[] {
  const value = scope.value
  if (scope.kind === 'datacenter') return entities.filter(entity => !value || entity.dcCode === value)
  if (scope.kind === 'hall') return entities.filter(entity => entity.hallId === value)
  if (scope.kind === 'module') return entities.filter(entity => entity.moduleId === value)
  if (scope.kind === 'row') return entities.filter(entity => entity.rowId === value)
  const query = (scope.query ?? '').trim().toLowerCase()
  return entities.filter(entity => !query || `${entity.id} ${entity.label ?? ''}`.toLowerCase().includes(query))
}

export function makeSavedQuerySnapshot(entities: BulkEntity[], scope: BulkScope): SavedQuerySnapshot {
  const selected = filterBulkScope(entities, scope)
  const rows = selected.map(entity => ({ id: entity.id, label: entity.label ?? '', revision: entity.revision ?? 0 }))
  return { ids: selected.map(entity => entity.id).sort(), fingerprint: JSON.stringify(rows.sort((a, b) => a.id.localeCompare(b.id))) }
}

export function detectSavedQueryDrift(before: SavedQuerySnapshot, after: SavedQuerySnapshot) {
  const beforeIds = new Set(before.ids)
  const afterIds = new Set(after.ids)
  return {
    drift: before.fingerprint !== after.fingerprint,
    added: after.ids.filter(id => !beforeIds.has(id)),
    removed: before.ids.filter(id => !afterIds.has(id)),
  }
}
